import { ProjectContext } from "context/ProjectProvider";
import {
  projectInitialState,
  ProjectIntegrationsTabSharedContext,
  ProjectIntegrationsTabStateReducer
} from "pages/project-setting-menu/project-integrations/project-integrations-tab-context";
import {
  IntegrationType,
  simplifyProjectIntegrationConfigs,
  simplifySubscriptionIntegrationConfigs,
  simplifySystemIntegrationConfigs
} from "pages/subscription-settings/integrations-tab-new/utils";
import { useContext, useEffect, useMemo, useReducer } from "react";
import { invokeACCProjectSyncJob } from "services/integration-service";
import { useCIQQuerySubscription } from "hooks/ciq-gql-hooks";
import {
  PROJECT_INTG_CONFIGS,
  SUBSCRIPTION_INTEGRATIONS,
  SUBSCRIPTION_INTG_CONFIGS,
  SYSTEM_INTEGRATION_CONFIGS
} from "services/graphQL/ciq-queries";
import { FormInstance } from "antd";
import IntegrationStepForm from "./initialForm";
import StepsLayout from "../layout-steps";
import WelcomeAutodeskInitialIntegration from "./autodesk-initial-integration";
import WelcomeProcoreInitialIntegration from "./procore-initial-integration";

export function IntegrationStep({
  integrationForm,
  setButtonEnable
}: {
  integrationForm: FormInstance<any>;
  setButtonEnable: React.Dispatch<React.SetStateAction<boolean>>;
}) {
  const { tokenRetrievalState, gqlClientForProject } =
    useContext(ProjectContext);

  const [sharedState, dispatch] = useReducer(
    ProjectIntegrationsTabStateReducer,
    projectInitialState
  );

  const providerValue: any = useMemo(
    () => ({
      sharedState,
      dispatch
    }),
    [sharedState]
  );

  const onProjectIntegrated = async (response: any) => {
    await invokeACCProjectSyncJob(
      {
        project_config_id: response.data[0]
      },
      tokenRetrievalState.token
    );
  };

  const { data: dataSystemIntgConfigs } = useCIQQuerySubscription(
    SYSTEM_INTEGRATION_CONFIGS,
    { client: gqlClientForProject }
  );

  const { data: dataAvailableIntegrations } = useCIQQuerySubscription(
    SUBSCRIPTION_INTEGRATIONS,
    { client: gqlClientForProject }
  );

  const { data: dataSubscriptionIntgConfigs } = useCIQQuerySubscription(
    SUBSCRIPTION_INTG_CONFIGS,
    { client: gqlClientForProject }
  );

  const { data: dataProjectIntgConfigs } = useCIQQuerySubscription(
    PROJECT_INTG_CONFIGS,
    {
      client: gqlClientForProject
    }
  );

  const subscriptionIntegrationConfigs = useMemo(() => {
    return simplifySubscriptionIntegrationConfigs(
      dataSubscriptionIntgConfigs?.subscription_integration_configs
    );
  }, [dataSubscriptionIntgConfigs]);

  const projectIntegrationConfigs = useMemo(() => {
    return simplifyProjectIntegrationConfigs(
      dataProjectIntgConfigs?.project_integration_configs
    );
  }, [dataProjectIntgConfigs]);

  const systemIntegrationConfigs = useMemo(() => {
    return simplifySystemIntegrationConfigs(
      dataSystemIntgConfigs?.system_integration_configs
    );
  }, [dataSystemIntgConfigs]);

  useEffect(() => {
    if (
      !dataAvailableIntegrations ||
      !projectIntegrationConfigs ||
      !systemIntegrationConfigs
    )
      return;
    dispatch({
      type: "SET_SHARED_STATE",
      payload: {
        subscriptionIntegrationConfigs,
        projectIntegrationConfigs,
        systemIntegrationConfigs,
        availableIntegrations: dataAvailableIntegrations.integrations
      }
    });
  }, [
    dataAvailableIntegrations,
    projectIntegrationConfigs,
    subscriptionIntegrationConfigs,
    systemIntegrationConfigs
  ]);

  return (
    <StepsLayout title="Integrate with your Project Management System">
      <ProjectIntegrationsTabSharedContext.Provider value={providerValue}>
        {sharedState.activeView === "list" && (
          <IntegrationStepForm setButtonEnable={setButtonEnable} />
        )}

        {sharedState.system === IntegrationType[IntegrationType.PROCORE] &&
          sharedState.activeView === "configure-initial" && (
            <WelcomeProcoreInitialIntegration
              onProjectIntegrated={onProjectIntegrated}
              integrationForm={integrationForm}
            />
          )}
        {sharedState.system === IntegrationType[IntegrationType.AUTODESK] &&
          sharedState.activeView === "configure-initial" && (
            <WelcomeAutodeskInitialIntegration
              onProjectIntegrated={onProjectIntegrated}
              integrationForm={integrationForm}
            />
          )}
      </ProjectIntegrationsTabSharedContext.Provider>
    </StepsLayout>
  );
}
